import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { defaultInstance } from '../api/httpClient';
import { toaster } from 'baseui/toast';
import { useBusinessContext } from '../context/BusinessContext';
import { useUserPermissions } from './useUserPermissions';

export type EventStatus = 'draft' | 'published' | 'archived';

export interface Event {
  id: string;
  name?: string;
  name_es?: string;
  name_en?: string;
  description_es?: string;
  description_en?: string;
  short_description_es?: string;
  short_description_en?: string;
  start_date?: string;
  end_date?: string;
  capacity?: number;
  active?: boolean;
  logo_url?: string;
  background_img_url?: string;
  /**
   * SAM-263 / SAM-710: dedicated mobile-format banner (4:5 portrait). The
   * column ships with SAM-268/SAM-713; the public detail page prefers it and
   * falls back to background_img_url.
   */
  banner_mobile_url?: string;
  phone_number?: string;
  address?: string;
  instagram_url?: string;
  website_url?: string;
  pin_code?: string;
  /**
   * SAM-714: publication state (SAM-713 adds the column, DB default
   * 'published'). Only Cognito groups `admin` / `xhinola_admin` may publish,
   * archive or edit a non-draft event — the CRUD lambda enforces it and forces
   * anyone else's create to 'draft'. Public /event/event returns only
   * published events. Read it through eventStatusOf() so rows from before the
   * migration still get a status.
   */
  status?: EventStatus;
  published_at?: string | null;
  published_by?: string | null;
  event_type_id?: number;
  event_group_id?: number;
  business_id?: string;
  tenant_id?: string;
  access_settings_id?: number;
  created_at?: string;
  updated_at?: string;
  event_type?: any;
  access_settings?: any;
  [key: string]: any;
}

export const useEvents = (options: { enabled?: boolean } = {}) => {
  const { currentBusinessId } = useBusinessContext();
  const { isAdmin, isLoading: permissionsLoading } = useUserPermissions();

  return useQuery({
    // Scoped by business so a user assigned to one business only sees that
    // business's events. Admins (full access) still see every event.
    queryKey: ['events', currentBusinessId, isAdmin],
    queryFn: async (): Promise<Event[]> => {
      let url = '/event?order_by=start_date&order_direction=DESC&limit=200&expand=event_type,event_type_id,access_settings,access_settings_id';
      if (!isAdmin && currentBusinessId) {
        url += `&business_id=${currentBusinessId}`;
      }

      const response = await defaultInstance({ method: 'GET', url });
      const data = (response as any)?.data?.data ?? (response as any)?.data ?? [];
      return data;
    },
    // Wait until permissions resolve so admins don't briefly fetch a
    // business-scoped list (which would then refetch once isAdmin flips true).
    enabled: !permissionsLoading && options.enabled !== false,
    staleTime: 30000,
  });
};

/**
 * Status of an event, tolerant of rows/APIs from before SAM-713: no `status`
 * means the old `active` flag decides (active → published, inactive → archived).
 */
export const eventStatusOf = (e: Pick<Event, 'status' | 'active'> | null | undefined): EventStatus => {
  if (e?.status === 'draft' || e?.status === 'published' || e?.status === 'archived') return e.status;
  return e?.active === false ? 'archived' : 'published';
};

// Single-event fetch used by the dashboard route. Pulls access_settings inline
// so we can read the configured companion template (adults/child/categories).
// Uses the LISTING endpoint with id filter (`/event?id=X`) because the singular
// `/event/{id}` doesn't honor `?expand=` reliably — we need access_settings
// embedded so the dashboard can label custom category buckets.
export const useEvent = (eventId: string | undefined) => {
  return useQuery({
    queryKey: ['event', eventId],
    queryFn: async (): Promise<any | null> => {
      if (!eventId) return null;
      const response = await defaultInstance({
        method: 'GET',
        url: `/event?id=${eventId}&limit=1&expand=event_type,event_type_id,access_settings,access_settings_id`,
      });
      const data = (response as any)?.data?.data ?? (response as any)?.data ?? null;
      const list = Array.isArray(data) ? data : [data];
      return list[0] || null;
    },
    enabled: !!eventId,
    staleTime: 30000,
  });
};

// Lists every business_access tied to a given event_id. JSONB fields
// (main_guest, business_guests) come back inline; we aggregate client-side.
export const useEventAccesses = (eventId: string | undefined) => {
  return useQuery({
    queryKey: ['eventAccesses', eventId],
    queryFn: async (): Promise<any[]> => {
      if (!eventId) return [];
      const response = await defaultInstance({
        method: 'GET',
        url: `/business_access?event_id=${eventId}&limit=20000&order_by=create_date&order_direction=DESC`,
      });
      const data = (response as any)?.data?.data ?? (response as any)?.data ?? [];
      return Array.isArray(data) ? data : [];
    },
    enabled: !!eventId,
    staleTime: 15000,
  });
};

// SAM-123: event types for the create/edit form's Type selector.
export interface EventType {
  id: number;
  name?: string;
}

export const useEventTypes = () => {
  return useQuery({
    queryKey: ['eventTypes'],
    queryFn: async (): Promise<EventType[]> => {
      const response = await defaultInstance({
        method: 'GET',
        url: '/event_type?order_by=name&order_direction=ASC&limit=500',
      });
      const data = (response as any)?.data?.data ?? (response as any)?.data ?? [];
      return Array.isArray(data) ? data : [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

// SAM-278: event groups for the create/edit form's Event Group selector.
export interface EventGroup {
  id: number;
  name?: string;
}

export const useEventGroups = () => {
  return useQuery({
    queryKey: ['eventGroups'],
    queryFn: async (): Promise<EventGroup[]> => {
      const response = await defaultInstance({
        method: 'GET',
        url: '/event_group?order_by=name&order_direction=ASC&limit=500',
      });
      const data = (response as any)?.data?.data ?? (response as any)?.data ?? [];
      return Array.isArray(data) ? data : [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useEventMutations = () => {
  const queryClient = useQueryClient();

  const createEvent = useMutation({
    mutationFn: async (data: Partial<Event>) => {
      const response = await defaultInstance({
        method: 'POST',
        url: '/event',
        data,
      });
      return (response as any)?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toaster.positive('Event created successfully', { autoHideDuration: 3000 });
    },
    onError: (err: any) => {
      toaster.negative(err?.message || 'Failed to create event', { autoHideDuration: 3000 });
    },
  });

  const updateEvent = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Event> }) => {
      const response = await defaultInstance({
        method: 'PUT',
        url: `/event/${id}`,
        data,
      });
      return (response as any)?.data;
    },
    onSuccess: (_data, { id, data }) => {
      // Write what was saved into the single-event cache right away, then refetch
      // even when no screen is observing it (refetchType 'all'): a publish from the
      // Preview page must show up in the editor it navigates to, not a cached draft.
      queryClient.setQueryData(['event', id], (old: Event | null | undefined) => (old ? { ...old, ...data } : old));
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event', id], refetchType: 'all' });
      toaster.positive('Event updated successfully', { autoHideDuration: 3000 });
    },
    onError: (err: any) => {
      toaster.negative(err?.message || 'Failed to update event', { autoHideDuration: 3000 });
    },
  });

  const deleteEvent = useMutation({
    mutationFn: async (id: string) => {
      const response = await defaultInstance({
        method: 'DELETE',
        url: `/event/${id}`,
      });
      return (response as any)?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toaster.positive('Event deleted successfully', { autoHideDuration: 3000 });
    },
    onError: (err: any) => {
      toaster.negative(err?.message || 'Failed to delete event', { autoHideDuration: 3000 });
    },
  });

  return {
    createEvent: createEvent.mutateAsync,
    updateEvent: updateEvent.mutateAsync,
    deleteEvent: deleteEvent.mutateAsync,
    isCreating: createEvent.isPending,
    isUpdating: updateEvent.isPending,
    isDeleting: deleteEvent.isPending,
  };
};
